import { getLocalStorage } from '../utils/web/storageAvailability';
// Shared deployment configuration types and base config

export type DeploymentTarget =
    | 'widget'
    | 'widget-test'
    | 'chrome-extension'
    | 'preprod'
    | 'backend-ui'
    | 'backoffice-playground'
    | 'demo-page';

export interface DeploymentConfig {
    target: DeploymentTarget;
    // Add other build-specific config here if needed
    // buildTime?: string;
    // gitCommit?: string;
}

// Private storage for deployment target
let _deploymentTarget: DeploymentTarget | undefined;
let _isInitialized = false;

// Immutable getter for deployment target
export const getDeploymentTarget = (): DeploymentTarget | undefined => {
    return _deploymentTarget;
};

export const isBackofficePlayground = (): boolean => {
    // The backoffice preview is a staff testing surface, not a production visitor surface.
    return getDeploymentTarget() === 'backoffice-playground';
};

// Staff preview surfaces (backoffice playground + standalone playground). On these
// the widget renders on its own origin, so same-origin ("local content") links must
// open in a new tab rather than navigating the preview away.
export const isPlaygroundSurface = (): boolean => {
    const target = getDeploymentTarget();
    return target === 'backoffice-playground' || target === 'preprod';
};

// Read-only property that exposes the deployment target
export const DEPLOYMENT_TARGET = new Proxy({} as { readonly value: DeploymentTarget | undefined }, {
    get(_target, prop) {
        if (prop === 'value') {
            return _deploymentTarget;
        }
        return undefined;
    },
    set() {
        throw new Error('DEPLOYMENT_TARGET is read-only. Use setDeploymentTarget() to initialize.');
    },
});

// Function to set deployment target (can only be called once)
export const setDeploymentTarget = (target: DeploymentTarget) => {
    if (_isInitialized) {
        throw new Error(
            `Deployment target already initialized as "${_deploymentTarget}". Cannot change to "${target}".`
        );
    }
    _deploymentTarget = target;
    _isInitialized = true;
};

// Function to update deployment target (allows changing after initialization)
// Use this for cases where deployment target needs to be updated dynamically (e.g., demo pages)
export const updateDeploymentTarget = (target: DeploymentTarget) => {
    _deploymentTarget = target;
    if (!_isInitialized) {
        _isInitialized = true;
    }
};

// Alias for shorter imports
export { setDeploymentTarget as setTarget };

// "Exclude my own visits from stats" switch, persisted per-browser.
// When set, the widget reports deployment_target = 'widget-test' instead of
// 'widget'. Dashboard stats RPCs and PostHog dashboards both filter on
// deployment_target = 'widget', so flagged traffic drops out of production
// numbers automatically. Toggled by ?rose_test=1|0 (widget boot) or by starting
// a chat message with // (send path).
const WIDGET_TEST_STORAGE_KEY = 'rose_widget_test';

export const isWidgetTestModeStored = (): boolean => {
    try {
        return getLocalStorage().getItem(WIDGET_TEST_STORAGE_KEY) === '1';
    } catch {
        // Storage disabled (Safari private mode, etc.) — treat as off.
        return false;
    }
};

export const setWidgetTestMode = (on: boolean): void => {
    try {
        if (on) {
            getLocalStorage().setItem(WIDGET_TEST_STORAGE_KEY, '1');
        } else {
            getLocalStorage().removeItem(WIDGET_TEST_STORAGE_KEY);
        }
    } catch {
        // Storage disabled — nothing to persist.
    }
};

// Chrome extension visual indicator state
// Allows users to hide the red border/banner by clicking on it
let _chromeIndicatorHidden = false;

export const setChromeIndicatorHidden = (hidden: boolean) => {
    _chromeIndicatorHidden = hidden;
};

export const isChromeIndicatorHidden = (): boolean => {
    return _chromeIndicatorHidden;
};
